import styled, { css, keyframes } from 'styled-components';
import {
  error,
  onSurface,
  onSurfaceVariant,
  outlineVariant,
  popoverShadow,
  primary,
  primaryTone,
  radiusFull,
  surface,
  surfaceContainer,
  surfaceHigh,
} from '../../themeTokens';
import { CHROME_INSET_PX } from '../chrome.constants';

const shimmer = keyframes`
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
`;

export const RenderSlot = styled.div`
  position: absolute;
  top: 50%;
  right: 14px;
  translate: 0 -50%;
  z-index: 3;
  pointer-events: auto;
`;

export const WandButton = styled.button<{ $open: boolean; $narrow: boolean }>`
  display: grid;
  place-items: center;
  width: ${(p) => (p.$narrow ? 40 : 48)}px;
  height: ${(p) => (p.$narrow ? 40 : 48)}px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  color: #fff;
  font-size: ${(p) => (p.$narrow ? 18 : 22)}px;
  cursor: pointer;
  background: linear-gradient(135deg, ${primaryTone(60)} 0%, ${primaryTone(50)} 45%, ${primaryTone(80)} 100%);
  background-size: 180% 180%;
  animation: ${shimmer} 6s ease-in-out infinite;
  box-shadow: ${(p) =>
    p.$open
      ? css`0 0 0 2px ${primary(p)}, 0 8px 28px color-mix(in srgb, ${primaryTone(50)(p)} 75%, transparent)`
      : css`0 0 0 1px rgb(255 255 255 / 0.18) inset, 0 6px 22px color-mix(in srgb, ${primaryTone(50)(p)} 55%, transparent)`};
  transition: transform 160ms ease, box-shadow 160ms ease;

  &:hover {
    transform: scale(1.08);
  }

  &:focus-visible {
    outline: 2px solid ${primary};
    outline-offset: 3px;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const Panel = styled.div<{ $narrow: boolean }>`
  z-index: 4;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  border: 1px solid ${outlineVariant};
  border-radius: 14px;
  background: ${surfaceContainer};
  color: ${onSurface};
  box-shadow: ${popoverShadow};
  font-size: 13px;
  pointer-events: auto;

  ${({ $narrow }) =>
    $narrow
      ? css`
          position: absolute;
          left: ${CHROME_INSET_PX}px;
          right: ${CHROME_INSET_PX}px;
          bottom: ${CHROME_INSET_PX + 52}px;
        `
      : css`
          position: absolute;
          top: 50%;
          right: calc(100% + 14px);
          translate: 0 -50%;
          width: 260px;
        `}
`;

export const SectionLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${onSurfaceVariant};
`;

export const ModelField = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const ModelTrigger = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  height: 32px;
  padding: 0 10px 0 12px;
  border: 1px solid ${outlineVariant};
  border-radius: 8px;
  background: ${surfaceHigh};
  color: ${onSurface};
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }

  &:focus-visible {
    outline: 2px solid ${primary};
    outline-offset: 1px;
  }
`;

export const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

export const Chip = styled.button<{ $active: boolean }>`
  padding: 4px 10px;
  border-radius: ${radiusFull};
  border: 1px solid ${(p) => (p.$active ? primary(p) : outlineVariant(p))};
  background: ${(p) => (p.$active ? `color-mix(in srgb, ${primary(p)} 18%, transparent)` : 'transparent')};
  color: ${onSurface};
  font: inherit;
  font-size: 12px;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${primary};
    outline-offset: 1px;
  }
`;

export const RenderAction = styled.button`
  height: 32px;
  border: 0;
  border-radius: 8px;
  background: ${primary};
  color: ${({ theme }) => theme.colors?.onPrimary ?? '#1b1b21'};
  font: inherit;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }

  &:focus-visible {
    outline: 2px solid ${onSurface};
    outline-offset: 2px;
  }
`;

export const ErrorText = styled.p`
  margin: 0;
  font-size: 12px;
  color: ${error};
`;

export const ResultLayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 6;
  display: grid;
  place-items: center;
  background: color-mix(in srgb, ${surface} 96%, transparent);
`;

export const ResultImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

export const ResultActions = styled.div`
  position: absolute;
  top: ${CHROME_INSET_PX}px;
  right: ${CHROME_INSET_PX}px;
  display: flex;
  gap: 8px;
`;

export const ResultButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 0;
  border-radius: ${radiusFull};
  background: ${surfaceHigh};
  color: ${onSurface};
  font: inherit;
  font-size: 13px;
  box-shadow: ${popoverShadow};
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${primary};
    outline-offset: 2px;
  }
`;
